import React, { useEffect, useImperativeHandle, useState } from 'react'
import { DataTable } from '../ui/DataTable'
import { Button } from '../ui/Button'

/**
 * MaterialTable-style API on top of the ChipMonk DataTable, so existing
 * screens keep working unchanged:
 *   columns[{ field, title, render(row, { rowIndex }), lookup, sorting, cellStyle, headerStyle }]
 *   options{ paging, pageSize, pageSizeOptions, sorting, selection, search }
 *   actions[{ icon() | icon, tooltip, onClick(event, selectedRows) }]
 */
const MaterialTableAdapter = ({
  columns = [],
  data = [],
  title,
  onRowClick,
  actions = [],
  options = {},
  onSearchChange,
  onSelectionChange,
  toolbarFilters,
  toolbarActions,
  tableRef,
  isLoading,
  description,
  emptyTitle,
  emptyDescription,
}) => {
  const [selectedRows, setSelectedRows] = useState([])

  const showSelection = options.selection === true

  useImperativeHandle(tableRef || null, () => ({
    selectAll: () => setSelectedRows(data),
    clearSelection: () => setSelectedRows([]),
    dataManager: {
      changeAllSelected: (checked) => setSelectedRows(checked ? data : []),
    },
  }), [data])

  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedRows)
  }, [selectedRows, onSelectionChange])

  const actionNodes = actions?.length ? (
    <>
      {actions.map((action, i) =>
        typeof action.icon === 'function' ? (
          <span key={i} onClick={(e) => action.onClick && action.onClick(e, selectedRows)} className="inline-flex">
            {action.icon()}
          </span>
        ) : (
          <Button
            key={i}
            size="sm"
            variant="secondary"
            onClick={(e) => action.onClick && action.onClick(e, selectedRows)}
          >
            {action.tooltip || action.title || 'Action'}
          </Button>
        ),
      )}
    </>
  ) : null

  const toolbarRight = toolbarActions || actionNodes ? (
    <>
      {toolbarActions}
      {actionNodes}
    </>
  ) : null

  return (
    <DataTable
      columns={columns.map((col) => ({ ...col, sortable: col.sorting !== false }))}
      data={data || []}
      title={title}
      description={description}
      toolbar={toolbarFilters}
      toolbarRight={toolbarRight}
      search={options.search !== false}
      onSearchChange={onSearchChange}
      sortable={options.sorting !== false}
      paginate={options.paging !== false}
      pageSize={options.pageSize || 10}
      pageSizeOptions={options.pageSizeOptions || [10, 20, 50]}
      dense={options.padding === 'dense'}
      loading={Boolean(isLoading)}
      onRowClick={onRowClick ? (e, row) => onRowClick(null, row) : undefined}
      selectable={showSelection}
      selectedRows={showSelection ? selectedRows : undefined}
      onSelectionChange={showSelection ? setSelectedRows : undefined}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
    />
  )
}

// Serial number column; rowIndex is the absolute index, so numbering continues across pages
export const serialNumberColumn = {
  field: 'serialNumber',
  title: '#',
  sorting: false,
  searchable: false,
  headerStyle: { width: 56, minWidth: 56 },
  cellStyle: { width: 56, minWidth: 56, color: 'var(--cm-text-muted)' },
  render: (rowData, { rowIndex }) => rowIndex + 1,
}

export default MaterialTableAdapter
